import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SummaryCards from './SummaryCards';
import CustomerActivity from './CustomerActivity';
import UsageChart from './UsageChart';
import RecentCustomers from './RecentCustomers';
import config from '../config';
import './Dashboard.css';

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [lastMonthCustomers, setLastMonthCustomers] = useState([]);
  const [lastWeekCustomers, setLastWeekCustomers] = useState([]);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date filter states
  const [usagePeriod, setUsagePeriod] = useState('last_month');
  const [customerPeriod, setCustomerPeriod] = useState('last_week');
  const [customDates, setCustomDates] = useState({ start_date: '', end_date: '' });

  const API_BASE_URL = config.API_BASE_URL;

  useEffect(() => {
    fetchData();
  }, [usagePeriod, customerPeriod, customDates]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query parameters for usage data
      const usageParams = new URLSearchParams({ period: usagePeriod });
      if (usagePeriod === 'custom' && customDates.start_date && customDates.end_date) {
        usageParams.append('start_date', customDates.start_date);
        usageParams.append('end_date', customDates.end_date);
      }
      
      // Build query parameters for customer data
      const customerParams = new URLSearchParams({ period: customerPeriod });
      
      const [
        summaryRes,
        lastMonthRes,
        lastWeekRes,
        dailyUsageRes,
        recentCustomersRes
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/summary.php`),
        axios.get(`${API_BASE_URL}/last-month.php`),
        axios.get(`${API_BASE_URL}/last-week.php`),
        axios.get(`${API_BASE_URL}/daily.php?${usageParams.toString()}`),
        axios.get(`${API_BASE_URL}/recent.php?${customerParams.toString()}`)
      ]);

      setSummary(summaryRes.data);
      setLastMonthCustomers(lastMonthRes.data);
      setLastWeekCustomers(lastWeekRes.data);
      setDailyUsage(dailyUsageRes.data);
      setRecentCustomers(recentCustomersRes.data);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard data. Please check if the server is running.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={fetchData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Program Usage Analytics</h2>
        <button onClick={fetchData} className="refresh-button">
          Refresh Data
        </button>
      </div>

      <SummaryCards summary={summary} />
      
      <div className="dashboard-grid">
        <div className="chart-container">
          <h3>Daily Usage Trends</h3>
          <UsageChart 
            data={dailyUsage} 
            period={usagePeriod}
            onPeriodChange={setUsagePeriod}
            customDates={customDates}
            onCustomDateChange={setCustomDates}
          />
        </div>

        <div className="chart-container">
          <h3>Last Customers by Company (with Access Count)</h3>
          <RecentCustomers 
            data={recentCustomers} 
            period={customerPeriod}
            onPeriodChange={setCustomerPeriod}
          />
        </div>

        <div className="chart-container">
          <h3>Active Customers - Last Month</h3>
          <CustomerActivity 
            data={lastMonthCustomers} 
            title="Monthly Activity"
          />
        </div>

        <div className="chart-container">
          <h3>Active Customers - Last Week</h3>
          <CustomerActivity 
            data={lastWeekCustomers} 
            title="Weekly Activity"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 